import { useCallback, useMemo, useState } from 'react';

const toYMD = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getWeekRange = (base: Date) => {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  const day = d.getDay(); // 0 Sun ... 6 Sat
  const diffToMonday = (day === 0 ? -6 : 1 - day);
  const start = new Date(d);
  start.setDate(d.getDate() + diffToMonday);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start: toYMD(start), end: toYMD(end) };
};

export function useWeekRange(initialDate: Date = new Date()) {
  const initial = useMemo(() => getWeekRange(initialDate), [initialDate]);
  const [start, setStart] = useState<string>(initial.start);
  const [end, setEnd] = useState<string>(initial.end);
  const formatLabel = (ymd: string) => {
    const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
    const date = new Date(y, m - 1, d);
    const day = String(date.getDate()).padStart(2, '0');
    const mon = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${mon}-${year}`;
  };

  const shift = useCallback((deltaDays: number) => {
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    s.setDate(s.getDate() + deltaDays);
    e.setDate(e.getDate() + deltaDays);
    setStart(toYMD(s));
    setEnd(toYMD(e));
  }, [start, end]);

  const handlePrevWeek = useCallback(() => shift(-7), [shift]);

  const handleNextWeek = useCallback(() => {
    const todayRange = getWeekRange(new Date());
    const nextEnd = new Date(end + 'T00:00:00');
    nextEnd.setDate(nextEnd.getDate() + 7);
    if (toYMD(nextEnd) <= todayRange.end) {
      shift(7);
    }
  }, [end, shift]);

  const goToCurrentWeek = useCallback(() => {
    const { start: s, end: e } = getWeekRange(new Date());
    setStart(s);
    setEnd(e);
  }, []);

  const setFromDate = useCallback((ymd: string) => {
    if (!ymd) return;
    const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
    const { start: s, end: e } = getWeekRange(new Date(y, m - 1, d));
    setStart(s);
    setEnd(e);
  }, []);

  const isNextDisabled = useMemo(() => {
    const todayRange = getWeekRange(new Date());
    return end >= todayRange.end;
  }, [end]);

  const formattedLabel = `${formatLabel(start)} to ${formatLabel(end)}`;
  const isCurrentWeek = useMemo(() => {
    const todayRange = getWeekRange(new Date());
    return start === todayRange.start && end === todayRange.end;
  }, [start, end]);

  return {
    start,
    end,
    label: `${start} to ${end}`,
    formattedLabel,
    handlePrevWeek,
    handleNextWeek,
    goToCurrentWeek,
    setFromDate,
    isNextDisabled,
    isCurrentWeek,
  };
}

export default useWeekRange;
